/**
 * Device revogado ou excluído no painel: toda rota autenticada do device passa a responder 401 e, sem isto, o
 * player ficava preso para sempre tocando o cache, com o heartbeat e o sync falhando em silêncio.
 *
 * - Só o heartbeat conta (hooks/use-heartbeat.ts). Depois de 2 heartbeats com 401 seguidos, sem 2xx no meio, o
 *   device é resetado. Um 401 isolado não basta: re-parear exige alguém na frente da tela. As outras rotas ficam de
 *   fora de propósito: uma rota que a API ainda não tem (player publicado antes da API) cai no requireAuth do painel
 *   e também responde 401, e despareia todas as TVs. O heartbeat sempre existe e passa por requireDeviceAuth.
 * - O reset apaga credenciais, agenda, fila de play logs e mídia em cache, e recarrega a página. Recarregar zera o
 *   estado em memória dos módulos (agenda, filas, blobs) e o App, sem credenciais, abre o pareamento. Agenda e mídia
 *   saem porque o próximo pareamento pode ser de outro cliente. Os play logs já não podem ser entregues: a API recusa
 *   o token antigo e recusaria os itens com o deviceId antigo sob o token novo.
 * - O hardwareId fica (chave própria no localStorage): é a identidade do aparelho, não do pareamento.
 */
import { useAuthStore } from '@/store/auth.store'
import { playerLogger } from '@/utils/player-logger'
import { getDB } from './db'

export const UNAUTHORIZED_RESET_THRESHOLD = 2

let consecutiveUnauthorized = 0
let resetting: Promise<void> | null = null

/** Apaga credenciais e dados locais do pareamento atual e recarrega o player. Chamadas repetidas viram uma só. */
export function resetDevice(reason: string): Promise<void> {
  if (resetting) return resetting
  resetting = (async () => {
    playerLogger.warn(`[device-reset] Resetando o device: ${reason}`)
    // Primeiro as credenciais: heartbeat e sync param de chamar a API enquanto o IndexedDB é limpo.
    useAuthStore.getState().clearCredentials()
    try {
      const db = await getDB()
      const tx = db.transaction(['schedule', 'playLogs', 'assets'], 'readwrite')
      await Promise.all([
        tx.objectStore('schedule').clear(),
        tx.objectStore('playLogs').clear(),
        tx.objectStore('assets').clear(),
        tx.done
      ])
    } catch (err) {
      // Sem credenciais o pareamento abre de qualquer forma. A agenda antiga fica no IDB (vale 24h) e pode tocar depois
      // do próximo pareamento até o primeiro sync; IDB que não limpa costuma nem abrir, então é o caso raro.
      playerLogger.error('[device-reset] Falha ao limpar o IndexedDB:', err)
    }
    window.location.reload()
  })()
  return resetting
}

/** Status HTTP de cada heartbeat (sucesso = 200). */
export function observeHeartbeatStatus(status: number): void {
  if (status === 401) {
    consecutiveUnauthorized++
    if (consecutiveUnauthorized >= UNAUTHORIZED_RESET_THRESHOLD) {
      void resetDevice(`${consecutiveUnauthorized} heartbeats com 401 seguidos`)
    }
  } else if (status >= 200 && status < 300) {
    consecutiveUnauthorized = 0
  }
}

/** Só para testes. */
export function resetDeviceResetStateForTests(): void {
  consecutiveUnauthorized = 0
  resetting = null
}
